import { v4 as uuidv4 } from 'uuid';

import { MEDIA_TYPE, STATUS_FAILED, STATUS_LOADED, STATUS_LOADING } from 'constants/MediaConstants';
import type { PayloadAction } from 'modules/global/generic.types';
import type { MayBe } from 'modules/global/typing/common/common';
import * as types from 'modules/guidelines/actionTypes';
import type { Guideline, GuidelineByIdState, GuidelineItem, GuidelineItemPreview } from 'modules/guidelines/types';
import type {
  DeleteGuidelineSuccessAction,
  PreviewGuidelineFailureAction,
  PreviewGuidelineSuccessAction,
} from 'modules/guidelines/types/actions';
import { indexByProp } from 'utils/global';
import { createReducer } from 'utils/ReduxUtils';

const initialState: GuidelineByIdState = {};

// API guideline as received, before normalization: nested dealer/group/createdBy/modifiedBy
// objects plus a HAL `link` envelope.
type ApiGuideline = Guideline & {
  dealer?: { id?: string };
  group?: { id?: string };
  link?: unknown;
  createdBy?: { id?: string };
  modifiedBy?: { id?: string };
};

// The API returns nested `dealer`/`group`/`createdBy`/`modifiedBy` objects; the BO keeps
// only their flat ids and drops the nested objects.
const cleanGuideline = (unformatted: ApiGuideline): Guideline => {
  const { dealer, group, link: _link, ...formatted } = unformatted;
  return {
    ...formatted,
    dealerId: formatted.dealerId ?? dealer?.id,
    groupId: formatted.groupId ?? group?.id,
    createdById: formatted.createdById ?? formatted.createdBy?.id,
    modifiedById: formatted.modifiedById ?? formatted.modifiedBy?.id,
  };
};

const updateGuidelines = (
  state: GuidelineByIdState,
  { payload }: PayloadAction<{ content: Guideline[] }>
): GuidelineByIdState => ({
  ...state,
  ...indexByProp(
    'id',
    payload.content.map((guideline: Guideline) => ({
      ...(state[guideline.id] ?? {}),
      ...cleanGuideline(guideline),
    }))
  ),
});

const updateGuideline = (state: GuidelineByIdState, { payload }: PayloadAction<Guideline>): GuidelineByIdState => ({
  ...state,
  [payload.id]: {
    ...(state[payload.id] ?? {}),
    ...cleanGuideline(payload),
  },
});

const removeGuidelineByGuidelineId = (
  state: GuidelineByIdState,
  { payload: guidelineId }: DeleteGuidelineSuccessAction
): GuidelineByIdState => {
  const newState = Object.values(state).filter(guideline => guideline.guidelineId !== guidelineId);
  return indexByProp('id', newState);
};

type BuildPreviews = (current?: Record<string, GuidelineItemPreview>) => MayBe<Record<string, GuidelineItemPreview>>;

// Rebuilds the previews of the targeted item, leaving the other items untouched.
const setItemPreviews = (items: MayBe<GuidelineItem[]>, itemId: string, buildPreviews: BuildPreviews): GuidelineItem[] =>
  (items ?? []).map(item =>
    (item.id === itemId
      ? { ...item, exampleMedia: { ...item.exampleMedia, previews: buildPreviews(item.exampleMedia?.previews) } }
      : item));

const applyPreviews = (
  state: GuidelineByIdState,
  guidelineId: string,
  itemId: string,
  buildPreviews: BuildPreviews
): GuidelineByIdState => {
  const guideline = state[guidelineId];
  if (!guideline) {
    return state;
  }
  return {
    ...state,
    [guidelineId]: {
      ...guideline,
      optional: setItemPreviews(guideline.optional, itemId, buildPreviews),
      mandatory: setItemPreviews(guideline.mandatory, itemId, buildPreviews),
    },
  };
};

const updatePreviewsSuccess = (state: GuidelineByIdState, { payload }: PreviewGuidelineSuccessAction): GuidelineByIdState => {
  const { guidelineId, itemId, previews } = payload;
  return applyPreviews(state, guidelineId, itemId, current =>
    Object.entries(previews).reduce<Record<string, GuidelineItemPreview>>(
      (acc, [brandId, path]) => ({
        // We don't pass `current` as the initial value of the reduce because we *want*
        // to overwrite the existing previews when the payload is empty
        ...current,
        ...acc,
        [brandId]: {
          ...acc[brandId],
          id: acc[brandId]?.id ?? uuidv4(),
          type: acc[brandId]?.type ?? MEDIA_TYPE.IMAGE,
          path,
          status: path ? STATUS_LOADED : STATUS_LOADING,
        },
      }),
      {}
    ));
};

const updatePreviewsFailure = (state: GuidelineByIdState, { payload }: PreviewGuidelineFailureAction): GuidelineByIdState => {
  const { guidelineId, itemId } = payload;
  return applyPreviews(state, guidelineId, itemId, (current = {}) =>
    Object.entries(current).reduce<Record<string, GuidelineItemPreview>>(
      (acc, [brandId, media]) => ({
        ...acc,
        [brandId]: media?.path
          ? media
          : {
            id: uuidv4(),
            path: media.path,
            status: STATUS_FAILED,
            type: MEDIA_TYPE.IMAGE,
          },
      }),
      {}
    ));
};

// Socket event (CLIPPED): converts the websocket payload to the canonical preview
// success payload and delegates.
const updatePreviewFromSocket = (
  state: GuidelineByIdState,
  { payload }: PayloadAction<{ entityId: string; data: { itemId: string; brandIds: string[]; path: string } }>
): GuidelineByIdState => {
  const { entityId, data } = payload;
  return updatePreviewsSuccess(state, {
    type: types.PREVIEW_GUIDELINE_SUCCESS,
    payload: {
      guidelineId: entityId,
      itemId: data.itemId,
      previews: data.brandIds.reduce<Record<string, string>>((acc, brandId) => ({ ...acc, [brandId]: data.path }), {}),
    },
  });
};

export default createReducer<GuidelineByIdState>(initialState, {
  [types.PUT_GUIDELINE_SUCCESS]: updateGuideline,
  [types.POST_GUIDELINE_SUCCESS]: updateGuideline,
  [types.FETCH_GUIDELINE_SUCCESS]: updateGuideline,
  [types.FETCH_GUIDELINES_SUCCESS]: updateGuidelines,
  [types.FIND_GUIDELINES_DEALER_OR_GROUP_SUCCESS]: updateGuidelines,
  [types.ASSIGN_BRANDS_TO_GUIDELINE_SUCCESS]: updateGuideline,
  [types.POST_CLONE_GUIDELINE_SUCCESS]: updateGuideline,
  [types.DELETE_GUIDELINE_SUCCESS]: removeGuidelineByGuidelineId,
  [types.PREVIEW_GUIDELINE_SUCCESS]: updatePreviewsSuccess,
  [types.PREVIEW_GUIDELINE_FAILURE]: updatePreviewsFailure,
  [types.PREVIEW_GUIDELINE_UPDATED]: updatePreviewFromSocket,
});
